import { isDefined } from "@clipboard-health/util-ts";
import { useFutureShifts } from "@src/appV2/redesign/WorkerShifts/api/useFutureShifts";
import { useNextTwoDaysShifts } from "@src/appV2/redesign/WorkerShifts/api/useNextTwoDaysShifts";
import { type Worker } from "@src/appV2/Worker/api/types";
import { compareAsc, parseISO } from "date-fns";
import { useCallback, useMemo } from "react";

export interface UseGetWorkerShiftsOptions {
  worker: Pick<Worker, "tmz">;
  enabled?: boolean;
}

export function useGetWorkerShifts(options: UseGetWorkerShiftsOptions) {
  const { worker, enabled = true } = options;

  const {
    isLoading: isLoadingNextTwoDaysShifts,
    isError: isErrorNextTwoDaysShifts,
    data: nextTwoDaysShiftsData,
    refetch: refetchNextTwoDaysShifts,
    remove: removeNextTwoDaysShifts,
  } = useNextTwoDaysShifts(worker.tmz ?? "", { enabled: isDefined(worker.tmz) && enabled });

  const {
    isLoading: isLoadingFutureShifts,
    isError: isErrorFutureShifts,
    data: futureShiftsData,
    refetch: refetchFutureShifts,
    remove: removeFutureShifts,
  } = useFutureShifts(worker.tmz ?? "", { enabled: isDefined(worker.tmz) && enabled });

  const refetch = useCallback(async () => {
    void refetchNextTwoDaysShifts();
    void refetchFutureShifts();
  }, [refetchNextTwoDaysShifts, refetchFutureShifts]);

  const remove = useCallback(() => {
    removeNextTwoDaysShifts();
    removeFutureShifts();
  }, [removeNextTwoDaysShifts, removeFutureShifts]);

  const shifts = useMemo(() => {
    const nextTwoDaysShifts = nextTwoDaysShiftsData?.response ?? [];
    const futureShifts = futureShiftsData?.response ?? [];

    return [...nextTwoDaysShifts, ...futureShifts].sort((a, b) => {
      return compareAsc(parseISO(a.start), parseISO(b.start));
    });
  }, [nextTwoDaysShiftsData, futureShiftsData]);

  const areShiftsLoading = isLoadingNextTwoDaysShifts || isLoadingFutureShifts;
  const isErrored = isErrorNextTwoDaysShifts || isErrorFutureShifts;

  return {
    shifts,
    isLoading: areShiftsLoading,
    isErrored,
    refetch,
    remove,
  };
}
